import React, { useCallback } from 'react'
import { NavLink, generatePath, useHistory } from 'react-router-dom'
import { useReactiveVar } from '@apollo/client'
import { mobileMenuOpenVar } from '@@partner:graphql/initial-data'
import { usePartner } from '@@partner:helpers/hooks/auth'
import { notifyError } from '@@partner:helpers/notification'

import CloseIcon from '@rsuite/icons/Close'
import ListIcon from '@rsuite/icons/List'
import TableIcon from '@rsuite/icons/Table'
import BranchIcon from '@rsuite/icons/Branch'
import Car from '@rsuite/icons/legacy/Car'
import CheckRoundIcon from '@rsuite/icons/CheckRound'
import TreeIcon from '@rsuite/icons/Tree'
import SpeakerIcon from '@rsuite/icons/Speaker'
import OperatePeopleIcon from '@rsuite/icons/OperatePeople'
import SettingHorizontalIcon from '@rsuite/icons/SettingHorizontal'
import Peoples from '@rsuite/icons/Peoples'
import Book2 from '@rsuite/icons/legacy/Book2'
import BarChartIcon from '@rsuite/icons/BarChart'
import OffIcon from '@rsuite/icons/Off'

import {
  ROUTE_SETTINGS,
  ROUTE_BRAND_SETTINGS,
  ROUTE_SHOPS,
  ROUTE_CATALOG,
  ROUTE_ORDERS,
  ROUTE_USERS_MANAGEMENT,
  ROUTE_MARKETING_MANAGEMENT,
  ROUTE_ASSEMBLING_MANAGEMENT,
  ROUTE_CUSTOMERS,
  ROUTE_TEXT_MATERIALS_MANAGEMENT,
  ROUTE_LOGISTIC_DASHBOARD,
  ROUTE_REPORTS,
  ROUTE_INDEX
} from '@@partner:config/routes'

import './MobileMenu.scss'

interface MenuItemProps {
  to: string
  icon: React.ReactNode
  label: string
  onClick: () => void
}

const MenuItem: React.FC<MenuItemProps> = ({ to, icon, label, onClick }) => (
  <NavLink
    to={to}
    className="MobileMenu__Item"
    activeClassName="MobileMenu__Item--active"
    onClick={onClick}
  >
    <span className="MobileMenu__ItemIcon">{icon}</span>
    <span className="MobileMenu__ItemLabel">{label}</span>
  </NavLink>
)

export default function MobileMenu () {
  const isOpen = useReactiveVar(mobileMenuOpenVar)
  const { brandSlug, partnerInfo, logout } = usePartner()
  const history = useHistory()

  const userName = [ partnerInfo?.first_name, partnerInfo?.last_name ].filter(Boolean).join(' ') || 'Пользователь'
  
  const userRole = (() => {
    const settings = partnerInfo?.settings ?? {}
    if (Array.isArray(settings.roles) && settings.roles.length) {
      return settings.roles[0]?.name ?? settings.roles[0]?.label ?? 'Менеджер'
    }
    return settings.roleName ?? settings.role ?? 'Менеджер'
  })()

  const canViewBlock = (permission: string) => 
    !partnerInfo.settings?.permissions || partnerInfo.settings?.permissions.includes(permission)

  const handleClose = useCallback(() => {
    mobileMenuOpenVar(false)
  }, [])

  const handleLogout = useCallback(() => {
    logout()
      .then(() => {
        mobileMenuOpenVar(false)
        history.push(ROUTE_INDEX)
      })
      .catch(() => {
        notifyError('Ошибка при выходе')
      })
  }, [ logout, history ])

  if (!isOpen) return null

  return (
    <div className="MobileMenu">
      <div className="MobileMenu__Header">
        <div className="MobileMenu__UserInfo">
          <span className="MobileMenu__UserName">{userName}</span>
          <span className="MobileMenu__UserRole">{userRole}</span>
        </div>
        <button className="MobileMenu__CloseBtn" onClick={handleClose}>
          <CloseIcon />
        </button>
      </div>

      <nav className="MobileMenu__Nav">
        {brandSlug && (
          <>
            {canViewBlock('customers') && (
              <MenuItem
                to={generatePath(ROUTE_CUSTOMERS, { brandSlug })}
                icon={<Peoples />}
                label="Покупатели"
                onClick={handleClose}
              />
            )}
            {canViewBlock('orders') && (
              <MenuItem
                to={generatePath(ROUTE_ORDERS, { brandSlug })}
                icon={<ListIcon />}
                label="Заказы"
                onClick={handleClose}
              />
            )}
            {canViewBlock('orders') && (
              <MenuItem
                to={generatePath(ROUTE_ASSEMBLING_MANAGEMENT, { brandSlug })}
                icon={<CheckRoundIcon />}
                label="Сборка"
                onClick={handleClose}
              />
            )}
            {canViewBlock('catalog') && (
              <MenuItem
                to={generatePath(ROUTE_CATALOG, { brandSlug })}
                icon={<TableIcon />}
                label="Каталог"
                onClick={handleClose}
              />
            )}
            {canViewBlock('settings') && (
              <MenuItem
                to={generatePath(ROUTE_BRAND_SETTINGS, { brandSlug })}
                icon={<BranchIcon />}
                label="Бренд"
                onClick={handleClose}
              />
            )}
            {canViewBlock('shops') && (
              <MenuItem
                to={generatePath(ROUTE_SHOPS, { brandSlug })}
                icon={<TreeIcon />}
                label="Магазины"
                onClick={handleClose}
              />
            )}
            {canViewBlock('logistics') && (
              <MenuItem
                to={generatePath(ROUTE_LOGISTIC_DASHBOARD, { brandSlug })}
                icon={<Car />}
                label="Логистика"
                onClick={handleClose}
              />
            )}
          </>
        )}

        {canViewBlock('users') && (
          <MenuItem
            to={generatePath(ROUTE_USERS_MANAGEMENT)}
            icon={<OperatePeopleIcon />}
            label="Сотрудники"
            onClick={handleClose}
          />
        )}

        {canViewBlock('marketing') && (
          <MenuItem
            to={generatePath(ROUTE_MARKETING_MANAGEMENT, { brandSlug })}
            icon={<SpeakerIcon />}
            label="Маркетинг"
            onClick={handleClose}
          />
        )}

        {brandSlug && canViewBlock('reports') && (
          <MenuItem
            to={generatePath(ROUTE_REPORTS, { brandSlug })}
            icon={<BarChartIcon />}
            label="Отчеты"
            onClick={handleClose}
          />
        )}

        {canViewBlock('textMaterials') && (
          <MenuItem
            to={generatePath(ROUTE_TEXT_MATERIALS_MANAGEMENT, { brandSlug })}
            icon={<Book2 />}
            label="Контент"
            onClick={handleClose}
          />
        )}

        {canViewBlock('settings') && (
          <MenuItem
            to={generatePath(ROUTE_SETTINGS)}
            icon={<SettingHorizontalIcon />}
            label="Настройки"
            onClick={handleClose}
          />
        )}
      </nav>

      <div className="MobileMenu__Footer">
        <button className="MobileMenu__LogoutBtn" onClick={handleLogout}>
          <OffIcon />
          <span>Выйти из аккаунта</span>
        </button>
      </div>
    </div>
  )
}
